import React from "react";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";

import TableHead from "./TableHead";
import ActionMenu from "components/ActionMenu";
import SkeletonTableRows from "./SkeletonTableRows";
import { MenuItem } from "components/ActionMenu/types";

import { ReactComponent as ActionsIcon } from "assets/images/actionMenu/actions-menu-icon.svg";

import { TableHeadCell } from "./types";

interface TableProps<T> {
  loading?: boolean;
  skeletonRowCount?: number;
  columns: TableHeadCell<T>[];
  rows: T[];
  actions?: MenuItem<T>[];
}

const ExtendedTable = <T extends { id: string }>(props: TableProps<T>) => {
  const { columns, loading, skeletonRowCount, rows, actions = [] } = props;

  const renderCell = (column: TableHeadCell<T>, row: T) => {
    const { id, align, width, color, sx, secure, transform, cell } = column;

    const value = transform ? transform(row[id], row) : row[id];

    return (
      <TableCell
        sx={{ width, maxWidth: 0, minWidth: width }}
        align={align}
        key={id.toString()}
      >
        {cell ? (
          cell(value, row)
        ) : (
          <Typography
            variant="body2"
            color={color}
            sx={sx}
            data-posthog-mask={secure ? "true" : undefined}
          >
            {String(value || "")}
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderAction = (row: T) => (
    <TableCell sx={{ width: "5%" }} key={`${row.id}-actions`} align="right">
      <ActionMenu
        mainIcon={<ActionsIcon />}
        menuId="action-menu"
        actions={actions}
        row={row}
      />
    </TableCell>
  );

  return (
    <TableContainer sx={{ mt: -1 }}>
      <Table>
        <TableHead<T> headCells={columns} withActions={actions.length > 0} />
        <TableBody>
          {loading ? (
            <SkeletonTableRows<T>
              columns={columns}
              rowCount={skeletonRowCount}
              withActions={actions.length > 0}
            />
          ) : (
            rows.map((row) => {
              return (
                <TableRow key={row.id}>
                  {columns.map((column) => renderCell(column, row))}
                  {actions?.length > 0 && renderAction(row)}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ExtendedTable;
